const REALTIME_URL = 'https://api.openai.com/v1/realtime?model=gpt-realtime-2';

const ROMAN_SCRIPT_INSTRUCTION =
	'IMPORTANT TRANSCRIPT RULE: All transcripts must be written in Latin/Roman script only. ' +
	'If the user or you speak Urdu, Hindi, Arabic, Punjabi, or any non-Latin language, ' +
	'transliterate it into Roman script (e.g., write "aap kaisay hain" instead of Urdu script, ' +
	'"shukriya" instead of Arabic script). Never output Urdu, Arabic, Devanagari, or any other ' +
	'non-Latin characters in the transcript. Speech audio can remain in the original language, ' +
	'but written transcripts must always use English letters.';

const TRANSCRIPTION_PROMPT =
	'Transcribe speech using Latin/Roman script only. If the speaker uses Urdu, Hindi, Arabic, ' +
	'or any non-Latin language, transliterate it into Roman script (Roman Urdu / Hinglish style). ' +
	'Examples: "aap kaisay hain", "main theek hoon", "shukriya". ' +
	'Never output Urdu, Arabic, Devanagari, or any other non-Latin characters.';

const LEAD_TOOL = {
	type: 'function',
	name: 'generate_lead',
	description:
		'Capture lead information from the conversation when a potential customer shares their contact details.',
	parameters: {
		type: 'object',
		properties: {
			name: { type: 'string', description: 'Full name of the lead' },
			email: { type: 'string', description: 'Email address' },
			phone: { type: 'string', description: 'Phone number' },
			company: { type: 'string', description: 'Company or organization name' }
		},
		required: ['name']
	}
};

class RealtimeSession {
	constructor(apiKey, systemPrompt, voice, callbacks = {}, options = {}) {
		this.apiKey = apiKey;
		this.systemPrompt = systemPrompt;
		this.voice = voice;
		this.callbacks = callbacks;
		this.interruptionsEnabled = options.interruptionsEnabled !== false;

		this.pc = null;
		this.dc = null;
		this.audioEl = null;
		this.localStream = null;
		this.connected = false;
	}

	_emit(name, ...args) {
		const cb = this.callbacks[name];
		if (typeof cb === 'function') {
			try {
				cb(...args);
			} catch (e) {
				// swallow callback errors so they don't crash the session
			}
		}
	}

	async connect() {
		try {
			this.pc = new RTCPeerConnection();

			this.audioEl = document.createElement('audio');
			this.audioEl.autoplay = true;

			this.pc.ontrack = event => {
				if (this.audioEl) {
					this.audioEl.srcObject = event.streams[0];
				}
			};

			this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
			this.localStream.getTracks().forEach(track => {
				this.pc.addTrack(track, this.localStream);
			});

			this.dc = this.pc.createDataChannel('oai-events');
			this.dc.onopen = () => {
				this.connected = true;
				this._emit('onConnectionChange', true);
				this._sendInitialSessionConfig();
			};
			this.dc.onclose = () => {
				this.connected = false;
				this._emit('onConnectionChange', false);
			};
			this.dc.onmessage = e => {
				try {
					const event = JSON.parse(e.data);
					this._handleServerEvent(event);
				} catch (err) {
					// ignore malformed messages
				}
			};

			const offer = await this.pc.createOffer();
			await this.pc.setLocalDescription(offer);

			const sdpResponse = await fetch(REALTIME_URL, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${this.apiKey}`,
					'Content-Type': 'application/sdp'
				},
				body: offer.sdp
			});

			if (!sdpResponse.ok) {
				const errText = await sdpResponse.text();
				throw new Error(`Realtime handshake failed: ${sdpResponse.status} ${errText}`);
			}

			const answerSdp = await sdpResponse.text();
			await this.pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
		} catch (err) {
			this._emit('onError', err && err.message ? err.message : 'Failed to connect');
			this.disconnect();
		}
	}

	_buildInstructions(prompt) {
		const base = prompt || 'You are a helpful assistant. Keep responses concise and conversational.';
		return `${base}\n\n${ROMAN_SCRIPT_INSTRUCTION}`;
	}

	_sendInitialSessionConfig() {
		this._sendEvent({
			type: 'session.update',
			session: {
				modalities: ['text', 'audio'],
				voice: this.voice,
				instructions: this._buildInstructions(this.systemPrompt),
				input_audio_transcription: {
					model: 'whisper-1',
					prompt: TRANSCRIPTION_PROMPT
				},
				turn_detection: {
					type: 'semantic_vad',
					interrupt_response: this.interruptionsEnabled
				},
				tools: [LEAD_TOOL]
			}
		});
	}

	_sendEvent(event) {
		if (this.dc && this.dc.readyState === 'open') {
			this.dc.send(JSON.stringify(event));
		}
	}

	_handleServerEvent(event) {
		switch (event.type) {
			case 'conversation.item.input_audio_transcription.completed':
				this._emit('onTranscriptUpdate', 'user', event.transcript, true);
				break;
			case 'response.audio_transcript.delta':
				this._emit('onTranscriptUpdate', 'assistant', event.delta, false);
				break;
			case 'response.audio_transcript.done':
				this._emit('onTranscriptUpdate', 'assistant', event.transcript, true);
				break;
			case 'response.audio.delta':
				this._emit('onAudioStart');
				break;
			case 'response.done':
				this._emit('onAudioEnd');
				this._handleToolCalls(event);
				break;
			case 'input_audio_buffer.speech_started':
				this._emit('onInterrupted');
				break;
			case 'error':
				this._emit('onError', (event.error && event.error.message) || 'Realtime error');
				break;
			default:
				break;
		}
	}

	_handleToolCalls(event) {
		const output = event && event.response && event.response.output;
		if (!Array.isArray(output)) return;

		output.forEach(item => {
			if (item.type === 'function_call' && item.name === 'generate_lead') {
				let args = {};
				try {
					args = JSON.parse(item.arguments || '{}');
				} catch (e) {
					args = {};
				}
				this._emit('onLeadCaptured', args);

				this._sendEvent({
					type: 'conversation.item.create',
					item: {
						type: 'function_call_output',
						call_id: item.call_id,
						output: JSON.stringify({ success: true, message: 'Lead captured successfully.' })
					}
				});
				this._sendEvent({ type: 'response.create' });
			}
		});
	}

	updateVoice(voice) {
		this.voice = voice;
		if (this.dc && this.dc.readyState === 'open') {
			this._sendEvent({ type: 'session.update', session: { voice } });
		}
	}

	updateInterruptions(enabled) {
		this.interruptionsEnabled = enabled;
		if (this.dc && this.dc.readyState === 'open') {
			this._sendEvent({
				type: 'session.update',
				session: {
					turn_detection: {
						type: 'semantic_vad',
						interrupt_response: enabled
					}
				}
			});
		}
	}

	updateSystemPrompt(prompt) {
		this.systemPrompt = prompt;
		if (this.dc && this.dc.readyState === 'open') {
			this._sendEvent({
				type: 'session.update',
				session: { instructions: this._buildInstructions(prompt) }
			});
		}
	}

	sendTextMessage(text) {
		if (!this.dc || this.dc.readyState !== 'open') return;
		this._sendEvent({
			type: 'conversation.item.create',
			item: {
				type: 'message',
				role: 'user',
				content: [{ type: 'input_text', text }]
			}
		});
		this._sendEvent({ type: 'response.create' });
	}

	disconnect() {
		try {
			if (this.localStream) {
				this.localStream.getTracks().forEach(track => track.stop());
				this.localStream = null;
			}
			if (this.dc) {
				try {
					this.dc.close();
				} catch (e) {}
				this.dc = null;
			}
			if (this.pc) {
				try {
					this.pc.close();
				} catch (e) {}
				this.pc = null;
			}
			if (this.audioEl) {
				this.audioEl.srcObject = null;
				this.audioEl = null;
			}
		} finally {
			if (this.connected) {
				this.connected = false;
				this._emit('onConnectionChange', false);
			}
		}
	}
}

export default RealtimeSession;
